// Supervisor home — cross-org review queue overview.
//
// SLP Professional shell (redesign V2): one SLP panel per project with a
// status lamp (amber = pending review, green = caught up), progress bar and
// mono stats. Data flow (useSupervisorProjects, role guard) unchanged.

import { EmptyState, SlpPanel, SlpStatusLamp, Spinner } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useSupervisorProjects } from '../../lib/api/supervisor'
import { useRequireRole } from '../../lib/auth'

export const Route = createFileRoute('/supervisor/')({
  component: SupervisorHome,
})

function SupervisorHome() {
  useRequireRole(['supervisor'])
  const { data, isLoading, error } = useSupervisorProjects()

  const projects = data?.projects ?? []

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-0.5">
        <h1 className="text-[16px] font-bold tracking-tight m-0">Supervisor console</h1>
        <p className="slp-mono m-0" style={{ fontSize: 10, color: 'var(--slp-text-dim)' }}>
          REVIEW QUEUE ACROSS EVERY ORGANIZATION
        </p>
      </header>

      {isLoading ? (
        <SlpPanel title="Projects">
          <div className="flex items-center gap-2" style={{ color: 'var(--slp-text-dim)' }}>
            <Spinner /> Loading projects…
          </div>
        </SlpPanel>
      ) : null}

      {error ? (
        <SlpPanel title="Projects">
          <div className="slp-row">
            <SlpStatusLamp status="warn" />
            <span>
              Couldn't load projects. {error instanceof Error ? error.message : 'Unknown error'} —
              check your connection and reload.
            </span>
          </div>
        </SlpPanel>
      ) : null}

      {data && projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="A producer needs to invite you to a project before it appears here."
        />
      ) : null}

      {projects.length > 0 ? (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
        >
          {projects.map((p) => {
            const pct = p.total_cues === 0 ? 0 : Math.round((p.approved / p.total_cues) * 100)
            const caughtUp = p.pending_supervisor === 0
            return (
              <Link
                key={p.project_id}
                to="/supervisor/projects/$projectId"
                params={{ projectId: p.project_id }}
                className="block no-underline text-inherit"
              >
                <SlpPanel title={p.project_code} className="h-full">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div
                        className="slp-mono truncate"
                        style={{
                          fontSize: 10,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--slp-text-dim)',
                        }}
                      >
                        {p.organization_name}
                      </div>
                      <h2 className="text-[14px] font-semibold m-0 mt-0.5 truncate text-[var(--text)]">
                        {p.project_name}
                      </h2>
                    </div>
                    <span className="slp-row" style={{ minHeight: 20, flex: 'none' }}>
                      <SlpStatusLamp status={caughtUp ? 'ok' : 'warn'} />
                      <span
                        className="slp-mono"
                        style={{
                          fontSize: 10,
                          color: caughtUp ? 'var(--slp-text-dim)' : 'var(--slp-text)',
                        }}
                      >
                        {caughtUp ? 'CAUGHT UP' : `${p.pending_supervisor} PENDING`}
                      </span>
                    </span>
                  </div>
                  {/* Progress bar — visual cue at a glance. */}
                  <div
                    className="mt-3 h-1.5 overflow-hidden"
                    style={{ background: 'var(--slp-panel-2)', borderRadius: 2 }}
                  >
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: pct === 100 ? 'var(--slp-lamp-green)' : 'var(--slp-blue)',
                      }}
                    />
                  </div>
                  <dl className="grid grid-cols-3 gap-2 mt-3 text-[12px]">
                    <Stat label="Approved" value={`${p.approved}/${p.total_cues}`} />
                    <Stat label="Pickups" value={String(p.pending_pickup)} />
                    <Stat label="Done" value={`${pct}%`} tone={pct === 100 ? 'green' : undefined} />
                  </dl>
                </SlpPanel>
              </Link>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'green' | 'amber'
}) {
  const color =
    tone === 'green'
      ? 'var(--slp-lamp-green)'
      : tone === 'amber'
        ? 'var(--slp-lamp-yellow)'
        : 'var(--slp-text)'
  return (
    <div>
      <dt
        className="slp-mono m-0"
        style={{
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--slp-text-dim)',
        }}
      >
        {label}
      </dt>
      <dd className="m-0 mt-0.5 font-semibold tabular-nums slp-mono" style={{ color }}>
        {value}
      </dd>
    </div>
  )
}
